import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { X } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { InstructorDisplay } from '@/components/ui/InstructorDisplay';
import { useI18n } from '@/contexts/I18nContext';
import { supabase } from '@/integrations/supabase/client';

interface LocalInstructor {
  id: string;
  db_id?: string; // real DB row id (undefined while optimistic)
  instructor_user_id?: string;
  manual_name?: string;
  name?: string;
  username?: string;
  avatar_url?: string;
  belt_rank?: string;
  is_manual: boolean;
}

interface SelectedInstructor {
  id: string;
  full_name?: string;
  name?: string;
  username?: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface EditInstructorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
}

export const EditInstructorsModal: React.FC<EditInstructorsModalProps> = ({
  isOpen,
  onClose,
  profileId,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();

  const [list, setList] = useState<LocalInstructor[]>([]);
  const [loading, setLoading] = useState(false);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      // Step 1: get profile_instructors rows
      const { data: rows, error } = await supabase
        .from('profile_instructors')
        .select('id, instructor_user_id, manual_name, created_at')
        .eq('profile_id', profileId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!rows || rows.length === 0) {
        if (isMounted.current) setList([]);
        return;
      }

      // Step 2: fetch profile details for non-manual rows
      const userIds = rows
        .filter((r: any) => r.instructor_user_id)
        .map((r: any) => r.instructor_user_id);

      let profilesMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, username, profile_picture_url, belt_rank')
          .in('id', userIds);

        (profiles || []).forEach((p: any) => { profilesMap[p.id] = p; });
      }

      const formatted: LocalInstructor[] = rows.map((row: any) => {
        if (row.instructor_user_id) {
          const p = profilesMap[row.instructor_user_id];
          return {
            id: row.id,
            db_id: row.id,
            instructor_user_id: row.instructor_user_id,
            name: p?.full_name || 'Unknown',
            username: p?.username,
            avatar_url: p?.profile_picture_url,
            belt_rank: p?.belt_rank,
            is_manual: false,
          };
        }
        return {
          id: row.id,
          db_id: row.id,
          manual_name: row.manual_name,
          is_manual: true,
        };
      });

      if (isMounted.current) setList(formatted);
    } catch (err) {
      console.error('Error fetching instructors:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (isOpen) fetchList();
  }, [isOpen, fetchList]);

  // ── Add ────────────────────────────────────────────────────────────────────
  const handleSelect = useCallback(async (instructor: SelectedInstructor | string | null) => {
    if (!instructor || !profileId) return;

    const isObject = typeof instructor === 'object';
    const isManualId = isObject && (instructor as SelectedInstructor).id?.startsWith('manual-');
    const isRealUser = isObject && !isManualId;

    // Optimistic entry — visible immediately
    const tempId = `temp-${Date.now()}`;
    const entry: LocalInstructor = isRealUser && isObject
      ? {
          id: tempId,
          instructor_user_id: (instructor as SelectedInstructor).id,
          name: (instructor as SelectedInstructor).full_name || (instructor as SelectedInstructor).name || '',
          username: (instructor as SelectedInstructor).username,
          avatar_url: (instructor as SelectedInstructor).profile_picture_url,
          belt_rank: (instructor as SelectedInstructor).belt_rank,
          is_manual: false,
        }
      : {
          id: tempId,
          manual_name:
            typeof instructor === 'string'
              ? instructor
              : (instructor as SelectedInstructor).full_name ||
                (instructor as SelectedInstructor).id.replace('manual-', ''),
          is_manual: true,
        };

    setList(prev => [...prev, entry]);
    setAutocompleteKey(k => k + 1);

    try {
      const insertData: Record<string, string> = { profile_id: profileId };

      if (isRealUser && isObject) {
        insertData.instructor_user_id = (instructor as SelectedInstructor).id;
      } else {
        const name =
          typeof instructor === 'string'
            ? instructor
            : (instructor as SelectedInstructor).full_name ||
              (instructor as SelectedInstructor).id.replace('manual-', '');
        insertData.manual_name = name
          .split(' ')
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');
      }

      const matchCol = insertData.instructor_user_id ? 'instructor_user_id' : 'manual_name';
      const matchVal = insertData.instructor_user_id || insertData.manual_name;

      // Duplicate check
      const { data: existing } = await supabase
        .from('profile_instructors')
        .select('id')
        .eq('profile_id', profileId)
        .eq(matchCol, matchVal!)
        .maybeSingle();

      if (!existing) {
        // INSERT without chaining .select() — avoids RLS SELECT vs INSERT mismatch
        const { error: insertError } = await supabase
          .from('profile_instructors')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      // Refresh list from DB to replace the temp entry with real data
      if (isMounted.current) await fetchList();

    } catch (err) {
      console.error('Error adding instructor:', err);
      if (isMounted.current) {
        setList(prev => prev.filter(i => i.id !== tempId));
        toast({
          title: t('common.error'),
          description: t('profile.error_adding_instructor'),
          variant: 'destructive',
        });
      }
    }
  }, [profileId, t, toast, fetchList]);

  // ── Remove ─────────────────────────────────────────────────────────────────
  const handleRemove = useCallback(async (item: LocalInstructor) => {
    setList(prev => prev.filter(i => i.id !== item.id));

    try {
      let deleteQuery = supabase
        .from('profile_instructors')
        .delete()
        .eq('profile_id', profileId);

      if (item.instructor_user_id) {
        deleteQuery = deleteQuery.eq('instructor_user_id', item.instructor_user_id) as typeof deleteQuery;
      } else if (item.manual_name) {
        deleteQuery = deleteQuery.eq('manual_name', item.manual_name) as typeof deleteQuery;
      } else if (item.db_id) {
        deleteQuery = deleteQuery.eq('id', item.db_id) as typeof deleteQuery;
      }

      const { error } = await deleteQuery;
      if (error) throw error;
    } catch (err) {
      console.error('Error removing instructor:', err);
      if (isMounted.current) {
        setList(prev => [...prev, item]);
        toast({
          title: t('common.error'),
          description: t('profile.error_removing_instructor'),
          variant: 'destructive',
        });
      }
    }
  }, [profileId, t, toast]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl overflow-visible">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_instructors')}</DialogTitle>
          <DialogDescription className="text-gray-600 text-sm">
            {t('profile.edit_instructors_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1 text-black font-sans">
              {t('profile.add_instructor')}
            </label>
            <InstructorAutocomplete
              key={autocompleteKey}
              value={null}
              onChange={handleSelect as any}
              placeholder="Type a name or a username"
              containerClassName="w-full"
              theme="light"
              disableBlurAdd
              noPortal
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-2 text-black font-sans">
              My Instructors ({list.length})
            </label>

            {loading ? (
              <div className="text-sm text-gray-500">Loading instructors...</div>
            ) : list.length === 0 ? (
              <div className="text-sm text-gray-500">
                No instructors added yet. Add your first instructor above.
              </div>
            ) : (
              <div className="space-y-2">
                {list.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 rounded-lg">
                    <InstructorDisplay
                      instructor={{
                        id: item.id,
                        name: item.name || item.manual_name,
                        full_name: item.name || item.manual_name,
                        username: item.username,
                        profile_picture_url: item.avatar_url,
                        belt_rank: item.belt_rank,
                      }}
                      size="md"
                    />
                    <button
                      onClick={() => handleRemove(item)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove instructor"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button
            variant="outline-light"
            onClick={handleClose}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
